import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MAP_AREAS, type AreaId, type MapArea } from "../../content/map";

type Props = {
  isUnlocked: (area: MapArea) => boolean;
  newlyUnlockedAreaId?: AreaId | null;
  onClearSequenceEnd?: () => void;
};

/**
 * FogOfWarLayer: 未解放エリアを包む柔らかな雲霧レイヤー。
 * - 未解放エリアの背景に雲霧を漂わせる。
 * - エリア解放時には霧が渦を巻いてふわっと拡散し、光とともに晴れる（解放演出の正）。
 * - 残ユニットバッジや操作UIを隠さないよう pointer-events-none で描画。
 */
export function FogOfWarLayer({
  isUnlocked,
  newlyUnlockedAreaId,
  onClearSequenceEnd,
}: Props) {
  const [clearingArea, setClearingArea] = useState<MapArea | null>(null);

  useEffect(() => {
    if (!newlyUnlockedAreaId) return;
    const area = MAP_AREAS.find((a) => a.id === newlyUnlockedAreaId);
    if (area) {
      setClearingArea(area);
      const timer = setTimeout(() => {
        setClearingArea(null);
        onClearSequenceEnd?.();
      }, 2600);
      return () => clearTimeout(timer);
    }
  }, [newlyUnlockedAreaId, onClearSequenceEnd]);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
      {/* 未解放エリアを包む静かな雲霧（柔らかなオーラ） */}
      {MAP_AREAS.map((area) => {
        const unlocked = isUnlocked(area);
        if (unlocked && clearingArea?.id !== area.id) return null;

        return (
          <div
            key={area.id}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{
              left: area.position.x,
              top: area.position.y,
              width: 240,
              height: 240,
            }}
          >
            {/* 雲霧の多層（静止。常時アニメは画面全体の描き直しになるため止めている） */}
            <div className="absolute inset-0 rounded-full bg-white/40 blur-xl" />
            <div className="absolute inset-4 rounded-full bg-stone-300/30 blur-2xl" />
            <div className="absolute inset-8 rounded-full bg-[#f4ecd8]/45 blur-lg" />
          </div>
        );
      })}

      {/* 霧晴れシークエンス（エリア解放時の祝祭アニメーション） */}
      <AnimatePresence>
        {clearingArea && (
          <motion.div
            key={`clearing-${clearingArea.id}`}
            initial={{ opacity: 1, scale: 0.8 }}
            animate={{ opacity: [1, 1, 0], scale: [0.8, 1.8, 2.5] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2.4, ease: "easeOut" }}
            className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none z-30"
            style={{
              left: clearingArea.position.x,
              top: clearingArea.position.y,
              width: 320,
              height: 320,
            }}
          >
            {/* 拡散する光のリング */}
            <motion.div
              animate={{ rotate: 360, scale: [1, 2] }}
              transition={{ duration: 2.2, ease: "easeOut" }}
              className="absolute inset-0 rounded-full border-4 border-amber-300/80 shadow-[0_0_60px_rgba(255,215,0,0.6)]"
            />
            {/* 雲の霧散 */}
            <div className="absolute inset-0 rounded-full bg-white/70 blur-2xl" />
            <div className="text-4xl animate-bounce">✨🌸✨</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
